import * as _ from 'lodash';
import moment from 'moment-timezone';

import { ImsObject } from './ims_object';
import { TicketResource } from '../resources';

export const IMS_TYPE__TICKET = 'ticket';

type TicketStatus = 'new' | 'acknowledged' | 'waiting' | 'resolved';
type TicketType = 'problem' | 'incident';

/**
 * @ngdoc service
 * @name ims.shared.data.service:Ticket
 * @description
 * The ims Ticket object.
 * Object structure:
 * {id: 1,
 *  resource:{}}
 *
 * @param {ngResource} ticketResource A ticket ngResource object.
 */
export class Ticket extends ImsObject {
    imsType: 'ticket';
    comments: { [x: number]: Comment };

    constructor(resource = new TicketResource()) {
        super(IMS_TYPE__TICKET, resource);

        this.comments = {};
        if (this.resource.assets === undefined) {
            this.resource.assets = [];
        }
        if (this.resource.ip_devices === undefined) {
            this.resource.ip_devices = [];
        }
        if (this.resource.zones === undefined) {
            this.resource.zones = [];
        }
        if (this.resource.assigned_users === undefined) {
            this.resource.assigned_users = [];
        }
        if (this.resource.notify_emails === undefined) {
            this.resource.notify_emails = '';
        }
    }

    get new(): boolean {
        return !this.id;
    }

    /**
     * Database primary key
     * @returns {number|*}
     */
    get id() {
        return this.resource.id;
    }

    /**
     * Set the db primary key. Should normally not be used
     * @param id {number|*}
     */
    set id(id) {
        this.resource.id = id;
    }

    get assetIds(): Array<number> {
        return this.resource.assets;
    }

    set assetIds(assetIds: Array<number>) {
        this.resource.assets = assetIds;
    }

    get ipDeviceIds(): Array<number> {
        return this.resource.ip_devices;
    }

    set ipDeviceIds(ipDeviceIds: Array<number>) {
        this.resource.ip_devices = ipDeviceIds;
    }

    get zoneIds(): Array<number> {
        return this.resource.zones;
    }

    set zoneIds(zoneIds: Array<number>) {
        this.resource.zones = zoneIds;
    }

    /**
     * Get the trigger id associated with this ticket
     */
    get triggerId(): number {
        return this.resource.trigger_id;
    }

    get triggerTrippedTimestamp() {
        return this.resource.trigger_tripped_timestamp;
    }

    set triggerTrippedTimestamp(value) {
        this.resource.trigger_tripped_timestamp = value;
    }

    /**
     * Set the trigger id
     * @param triggerId
     */
    set triggerId(triggerId: number) {
        this.resource.trigger_id = triggerId;
    }

    /**
     * Get the parent ticket id associated with this ticket
     */
    get parentId() {
        return this.resource.parent_id;
    }

    /**
     * Set the parent ticket id
     * @param parent
     */
    set parentId(parentId) {
        this.resource.parent_id = parentId;
    }

    /**
     * Get the status of the ticket, i.e. new, acknowledged, waiting or resolved
     */
    get status(): TicketStatus {
        return this.resource.status;
    }

    /**
     * Set the status of the ticket
     * @param status
     */
    set status(status: TicketStatus) {
        this.resource.status = status;
    }

    /**
     * Get the subject of the ticket, a string
     * @returns {Ticket.subject|Certificate|*|string|string}
     */
    get subject(): string {
        return this.resource.subject;
    }

    /**
     * @param subj {Ticket.subject|Certificate|*|string|string}
     */
    set subject(subj: string) {
        this.resource.subject = subj;
    }

    /**
     * Expose ticket subject as name so we can treat tickets generally like other
     * ims objects e.g. asset, ipdevice, zone
     * @returns {string}
     */
    get name(): string {
        return this.resource.subject;
    }

    /**
     * Is this ticket archived?
     * @returns {boolean|*}
     */
    get isArchived(): boolean {
        return this.resource.is_archived;
    }

    /**
     * @param archived {boolean|*}
     */
    set isArchived(archived: boolean) {
        this.resource.is_archived = archived;
    }

    /**
     * Is this ticket deleted?
     * @returns {boolean|*}
     */
    get isDeleted(): boolean {
        return this.resource.is_deleted;
    }

    /**
     * @param deleted {boolean|*}
     */
    set isDeleted(deleted: boolean) {
        this.resource.is_deleted = deleted;
    }

    /**
     * Get a csv string of emails
     * @returns {*|string|string}
     */
    get notifyEmails(): string {
        return this.resource.notify_emails;
    }

    /**
     * Set a csv string of emails
     * @param emailCsv {*|string|string}
     */
    set notifyEmails(emailCsv: string) {
        this.resource.notify_emails = emailCsv;
    }

    get priority(): number {
        return this.resource.priority;
    }

    set priority(value: number) {
        this.resource.priority = value;
    }

    get type(): TicketType {
        return this.resource.type;
    }

    set type(value: TicketType) {
        this.resource.type = value;
    }

    get body(): string {
        return this.resource.body;
    }

    set body(value: string) {
        this.resource.body = value;
    }

    get processing(): boolean {
        return this.resource.processing;
    }

    set processing(value: boolean) {
        this.resource.processing = value;
    }

    get created(): string {
        return this.resource.created;
    }

    set created(value: string) {
        this.resource.created = value;
    }

    get startTimestamp(): string {
        return this.resource.start_timestamp;
    }

    set startTimestamp(value: string) {
        this.resource.start_timestamp = value;
    }

    get endTimestamp(): string {
        return this.resource.end_timestamp;
    }

    set endTimestamp(value: string) {
        this.resource.end_timestamp = value;
    }

    get assignedUserIds(): Array<number> {
        return this.resource.assigned_users;
    }

    set assignedUserIds(assignedUserIdArray: Array<number>) {
        this.resource.assigned_users = assignedUserIdArray;
    }

    get lastUpdated(): string {
        return this.resource.last_updated;
    }

    /**
     * Clone this object
     * @returns {ims.shared.data.Ticket}
     */
    clone() {
        return new Ticket(_.clone(this.resource));
    }

    /**
     * Return array of property names that dont match
     * @param otherTicket Ticket object
     */
    diff(otherTicket) {
        if (otherTicket.constructor === Ticket) {
            return [
                'id',
                'assetIds',
                'ipDeviceIds',
                'zoneIds',
                'triggerId',
                'parentId',
                'subject',
                'isArchived',
                'status',
                'priority',
                'notifyEmails',
                'body',
                'type',
                'startTimestamp',
                'endTimestamp',
                'assignedUserIds',
            ].filter((key) => {
                if (key === 'startTimestamp' || key === 'endTimestamp') {
                    // We can get into the situation where the old timestamp is a moment while the
                    // new timestamp is a javascript date object. 'cast' both to a moment so we can
                    // call unix.
                    const thisTime = this[key] ? moment(this[key]).unix() : null;
                    const otherTime = otherTicket[key] ? moment(otherTicket[key]).unix() : null;
                    return thisTime !== otherTime;
                } else if (
                    key === 'assetIds' ||
                    key === 'ipDeviceIds' ||
                    key === 'zoneIds' ||
                    key === 'assignedUserIds'
                ) {
                    return !_.isEqual(this[key].sort(), otherTicket[key].sort());
                } else {
                    return otherTicket[key] !== this[key];
                }
            });
        } else {
            throw new Error('Can only diff with a Ticket');
        }
    }

    static clone(ticket: Ticket): Ticket {
        return ticket.clone();
    }

    static diff(thisTicket: Ticket, otherTicket: Ticket) {
        return thisTicket.diff(otherTicket);
    }
}
